"use client";

import { importWithChunkRecovery } from "@/lib/chunk-load-utils";
import type { AbstractIntlMessages } from "next-intl";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import {
  type ComponentType,
  type ReactElement,
  type ReactNode,
  use,
  useEffect,
} from "react";
import { IntlMessagesProvider } from "./intl-messages-provider";
import type { Locale } from "./locales";
import { isWebsitePage } from "./website-messages";

interface CatalogModule {
  readonly messages: AbstractIntlMessages;
}

const CATALOG_LOADERS: Readonly<Record<Locale, () => Promise<CatalogModule>>> =
  {
    de: () => import("./intl-catalog-de"),
    en: () => import("./intl-catalog-en"),
  };

// Only for next/dynamic's SSR chunk preload; messages come through use() below.
const DeCatalogPreload = dynamic(() =>
  import("./intl-catalog-de").then((m) => m.DeCatalogPreload),
);
const EnCatalogPreload = dynamic(() =>
  import("./intl-catalog-en").then((m) => m.EnCatalogPreload),
);

const CATALOG_PRELOADS: Readonly<Record<Locale, ComponentType>> = {
  de: DeCatalogPreload,
  en: EnCatalogPreload,
};

const catalogLoads = new Map<Locale, Promise<AbstractIntlMessages>>();

/** One promise per locale: use() needs the same thenable on every render. Failed chunk → one reload. */
function loadCatalog(locale: Locale): Promise<AbstractIntlMessages> {
  const cached = catalogLoads.get(locale);
  if (cached) return cached;
  const load = importWithChunkRecovery(CATALOG_LOADERS[locale]).then(
    (m) => m.messages,
    (reason: unknown) => {
      catalogLoads.delete(locale);
      throw reason;
    },
  );
  catalogLoads.set(locale, load);
  return load;
}

/**
 * Client provider; a function cannot cross from the server layout.
 * `messages` null → full catalog from the cached chunk; set → website subset or dev fallback.
 */
export function IntlProvider({
  locale,
  messages,
  timeZone,
  websiteOnly,
  children,
}: {
  readonly locale: Locale;
  readonly messages: AbstractIntlMessages | null;
  readonly timeZone: string;
  readonly websiteOnly: boolean;
  readonly children: ReactNode;
}): ReactElement {
  // Root layout never re-renders on client navigation: leaving the website reloads for the full catalog.
  const pathname = usePathname();
  const needsFullCatalog = websiteOnly && !isWebsitePage(pathname);
  useEffect(() => {
    if (needsFullCatalog) window.location.reload();
  }, [needsFullCatalog]);
  const content = needsFullCatalog ? null : children;
  // Same provider for every locale: a language switch re-renders the app, never remounts it.
  const resolvedMessages = messages ?? use(loadCatalog(locale));
  const CatalogPreload = CATALOG_PRELOADS[locale];
  return (
    <>
      {messages === null ? <CatalogPreload /> : null}
      <IntlMessagesProvider
        locale={locale}
        messages={resolvedMessages}
        timeZone={timeZone}
      >
        {content}
      </IntlMessagesProvider>
    </>
  );
}
